import { useState, useEffect } from "react";
import { Card, Button, Tag, Popconfirm, message, Modal, Descriptions, Row, Col, Empty, Spin, Tooltip, Input, Checkbox } from "antd";
import { 
    DeleteOutlined, 
    EyeOutlined, 
    PlayCircleOutlined,
    ReloadOutlined,
    FilePdfOutlined,
    FontSizeOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    LoadingOutlined,
    ZoomInOutlined,
    BorderOutlined,
    SettingOutlined,
    SearchOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getTemplates, deleteTemplate } from "../../core/api/pdfParser";
import useGlobalFilters from "../../hooks/useGlobalFilters";

function TemplateList() {
    const navigate = useNavigate();
    const { brandIds, hasSelectedBrands } = useGlobalFilters();
    const currentBrandId = brandIds.length > 0 ? brandIds[0] : null;
    
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);

    const filteredTemplates = templates.filter((t) => {
        if (!searchText) return true;
        const q = searchText.toLowerCase();
        return (
            (t.name || "").toLowerCase().includes(q) ||
            (t.filename || "").toLowerCase().includes(q)
        );
    });

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleBulkDelete = async () => {
        for (const id of selectedIds) {
            await handleDelete(id);
        }
        setSelectedIds([]);
    };

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const result = await getTemplates(currentBrandId);
            setTemplates(result.templates || []);
        } catch (err) {
            message.error("Failed to fetch templates");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, [currentBrandId]);

    const handleDelete = async (templateId) => {
        try {
            await deleteTemplate(templateId);
            message.success("Template deleted");
            fetchTemplates();
        } catch (err) {
            message.error(err.message || "Failed to delete template");
        }
    };

    const openPreview = async (template) => {
        setPreviewTemplate(template);
        setPreviewVisible(true);
        setPreviewLoading(true);
        setPreviewUrl(null);

        try {
            const token = localStorage.getItem("accessToken");
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/pdf-parser/templates/${template.id}/file/`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Failed to fetch PDF");
            }

            const ct = response.headers.get("content-type") || "";
            let blob;
            if (ct.includes("application/json")) {
                const data = await response.json();
                const s3Url = data?.download_url || data?.data?.download_url;
                if (s3Url) {
                    const s3Resp = await fetch(s3Url);
                    if (!s3Resp.ok) throw new Error("Failed to fetch PDF from storage");
                    blob = await s3Resp.blob();
                } else {
                    throw new Error("No download URL in response");
                }
            } else {
                blob = await response.blob();
            }
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
        } catch (err) {
            console.error("Error loading PDF preview:", err);
            message.error("Failed to load PDF preview");
        } finally {
            setPreviewLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handlePreviewClose = () => {
        setPreviewVisible(false);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    };

    const TemplateCard = ({ template }) => {
        const [thumbUrl, setThumbUrl] = useState(null);
        const [thumbLoading, setThumbLoading] = useState(true);
        const [thumbError, setThumbError] = useState(false);
        
        const placeholderCount = template.placeholders?.length || 0;
        const hasColumnMap = Object.keys(template.column_map || {}).length > 0;
        const hasFontMap = Object.keys(template.font_map || {}).length > 0;
        const customRegions = template.ignore_width?._customRegions || [];
        const hasCustomRegions = customRegions.length > 0;
        const isConfigured = hasColumnMap || hasFontMap || hasCustomRegions;
        const hasContent = template.has_file;
        
        // Get unique fonts used
        const uniqueFonts = [...new Set(template.placeholders?.map(p => p.font) || [])];
        
        // Fetch thumbnail with auth
        useEffect(() => {
            let mounted = true;
            
            if (!hasContent) {
                setThumbLoading(false);
                setThumbError(true);
                return;
            }
            
            const fetchThumbnail = async () => {
                try {
                    const token = localStorage.getItem("accessToken");
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/pdf-parser/templates/${template.id}/thumbnail/`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    
                    if (!response.ok) throw new Error("Failed to fetch thumbnail");
                    
                    let blob;
                    const ct = response.headers.get("content-type") || "";
                    if (ct.includes("application/json")) {
                        const data = await response.json();
                        const s3Url = data?.download_url || data?.data?.download_url;
                        if (!s3Url) throw new Error("No URL");
                        const s3Resp = await fetch(s3Url);
                        blob = await s3Resp.blob();
                    } else {
                        blob = await response.blob();
                    }
                    if (mounted) {
                        setThumbUrl(URL.createObjectURL(blob));
                        setThumbLoading(false);
                    }
                } catch (err) {
                    if (mounted) {
                        setThumbError(true);
                        setThumbLoading(false);
                    }
                }
            };
            
            fetchThumbnail();
            
            return () => {
                mounted = false;
                if (thumbUrl) {
                    URL.revokeObjectURL(thumbUrl);
                }
            };
        }, [template.id, hasContent]);
        
        return (
            <Card
                hoverable
                style={{ 
                    height: "100%",
                    borderRadius: 12,
                    overflow: "hidden",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                }}
                cover={
                    <div 
                        style={{ 
                            height: 180, 
                            background: "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            position: "relative",
                            overflow: "hidden"
                        }}
                        onClick={() => openPreview(template)}
                    >
                        <Checkbox
                            checked={selectedIds.includes(template.id)}
                            onChange={(e) => {
                                e.stopPropagation();
                                toggleSelect(template.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{ position: "absolute", top: 8, left: 8, zIndex: 5 }}
                        />
                        {thumbLoading ? (
                            <LoadingOutlined style={{ fontSize: 32, color: "#1a1a2e" }} spin />
                        ) : thumbUrl && !thumbError ? (
                            <img
                                src={thumbUrl}
                                alt={template.name}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    objectPosition: "top center"
                                }}
                            />
                        ) : !hasContent ? (
                            <>
                                <WarningOutlined style={{ fontSize: 48, color: "#faad14" }} />
                                <span style={{ color: "#faad14", fontSize: 12, marginTop: 8, fontWeight: 600 }}>
                                    DELETE & RE-UPLOAD
                                </span>
                            </>
                        ) : (
                            <>
                                <FilePdfOutlined style={{ fontSize: 48, color: "#1a1a2e" }} />
                                <span style={{ color: "#888", fontSize: 12, marginTop: 8 }}>
                                    Click to preview
                                </span>
                            </>
                        )}
                        
                        {/* Hover overlay */}
                        <div 
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "rgba(0,0,0,0.5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0,
                                transition: "opacity 0.2s ease",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                        >
                            <ZoomInOutlined style={{ fontSize: 32, color: "#fff" }} />
                        </div>
                        
                        {/* Status badges */}
                        <div style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            display: "flex",
                            gap: 4
                        }}>
                            {!hasContent && (
                                <Tooltip title="Re-upload needed - old template">
                                    <Tag color="warning" style={{ margin: 0 }}>
                                        <WarningOutlined /> Re-upload
                                    </Tag>
                                </Tooltip>
                            )}
                            {isConfigured && (
                                <Tooltip title={`Configured: ${hasCustomRegions ? customRegions.length + ' regions' : ''}${hasColumnMap ? ' • Column mapping' : ''}${hasFontMap ? ' • Font mapping' : ''}`}>
                                    <Tag color="green" style={{ margin: 0 }}>
                                        <SettingOutlined /> Ready
                                    </Tag>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                }
                actions={hasContent ? [
                    <Tooltip key="view" title="View Details">
                        <EyeOutlined onClick={() => {
                            setSelectedTemplate(template);
                            setDetailsVisible(true);
                        }} />
                    </Tooltip>,
                    <Tooltip key="generate" title="Generate PDFs">
                        <PlayCircleOutlined 
                            style={{ color: "#52c41a" }}
                            onClick={() => navigate(`/pdf-parser/generate?templateId=${template.id}`)} 
                        />
                    </Tooltip>,
                    <Popconfirm
                        key="delete"
                        title="Delete this template?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDelete(template.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <DeleteOutlined style={{ color: "#ff4d4f" }} />
                    </Popconfirm>,
                ] : [
                    <Popconfirm
                        key="delete"
                        title="Delete this old template?"
                        description="You need to re-upload this template."
                        onConfirm={() => handleDelete(template.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
                            Delete
                        </Button>
                    </Popconfirm>,
                ]}
            >
                <Card.Meta
                    title={
                        <Tooltip title={template.name}>
                            <span style={{ 
                                display: "block", 
                                overflow: "hidden", 
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontWeight: 600
                            }}>
                                {template.name}
                            </span>
                        </Tooltip>
                    }
                    description={
                        <div style={{ fontSize: 12 }}>
                            <div style={{ 
                                overflow: "hidden", 
                                textOverflow: "ellipsis", 
                                whiteSpace: "nowrap",
                                color: "#888",
                                marginBottom: 8
                            }}>
                                {template.filename}
                            </div>
                            
                            {/* Placeholder & Region count */}
                            <div style={{ marginBottom: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                                <Tag color="blue" style={{ margin: 0 }}>
                                    {placeholderCount} placeholders
                                </Tag>
                                {hasCustomRegions && (
                                    <Tag color="purple" style={{ margin: 0 }}>
                                        <BorderOutlined /> {customRegions.length} regions
                                    </Tag>
                                )}
                            </div>
                            
                            {/* Font info */}
                            {uniqueFonts.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                    {uniqueFonts.slice(0, 2).map((font, idx) => (
                                        <Tag key={idx} style={{ fontSize: 10, margin: 0 }}>
                                            <FontSizeOutlined style={{ marginRight: 2 }} />
                                            {font?.length > 15 ? font.slice(0, 15) + "..." : font}
                                        </Tag>
                                    ))}
                                    {uniqueFonts.length > 2 && (
                                        <Tag style={{ fontSize: 10, margin: 0 }}>
                                            +{uniqueFonts.length - 2} more
                                        </Tag>
                                    )}
                                </div>
                            )}
                        </div>
                    }
                />
            </Card>
        );
    };

    // If no brand selected, show message
    if (!hasSelectedBrands) {
        return (
            <Card style={{ textAlign: "center", padding: 60 }}>
                <Empty 
                    description="Please select a brand to view templates"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    return (
        <div>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <h5 style={{ margin: 0, fontWeight: 600 }}>
                    Saved Templates ({searchText ? `${filteredTemplates.length} / ${templates.length}` : templates.length})
                </h5>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Input.Search
                        placeholder="Search templates..."
                        size="small"
                        allowClear
                        prefix={<SearchOutlined />}
                        style={{ width: 250 }}
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchTemplates}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            {selectedIds.length > 0 && (
                <div style={{
                    marginBottom: 12,
                    padding: "8px 12px",
                    background: "#f0f5ff",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 12
                }}>
                    <Checkbox
                        checked={selectedIds.length === filteredTemplates.length && filteredTemplates.length > 0}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredTemplates.length}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setSelectedIds(filteredTemplates.map((t) => t.id));
                            } else {
                                setSelectedIds([]);
                            }
                        }}
                    >
                        Select All
                    </Checkbox>
                    <span style={{ fontSize: 13, color: "#555" }}>
                        {selectedIds.length} selected
                    </span>
                    <Popconfirm
                        title={`Delete ${selectedIds.length} template(s)?`}
                        description="This action cannot be undone."
                        onConfirm={handleBulkDelete}
                        okText="Delete All"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                            Delete Selected
                        </Button>
                    </Popconfirm>
                    <Button size="small" onClick={() => setSelectedIds([])}>
                        Clear
                    </Button>
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                    <Spin size="large" />
                </div>
            ) : templates.length === 0 ? (
                <Card style={{ textAlign: "center", padding: 40 }}>
                    <Empty 
                        description="No templates uploaded yet"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                        <Button 
                            type="primary" 
                            onClick={() => navigate("/pdf-parser")}
                            style={{ marginTop: 16 }}
                        >
                            Upload Your First Template
                        </Button>
                    </Empty>
                </Card>
            ) : filteredTemplates.length === 0 ? (
                <Card style={{ textAlign: "center", padding: 40 }}>
                    <Empty 
                        description="No templates match your search"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                </Card>
            ) : (
                <Row gutter={[16, 16]}>
                    {filteredTemplates.map((template) => (
                        <Col key={template.id} xs={24} sm={12} md={8} lg={6} xl={6}>
                            <TemplateCard template={template} />
                        </Col>
                    ))}
                </Row>
            )}

            {/* Template Details Modal */}
            <Modal
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: "#1a1a2e",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <FilePdfOutlined style={{ color: "#fff", fontSize: 20 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 600 }}>{selectedTemplate?.name}</div>
                            <div style={{ fontSize: 12, color: "#888" }}>{selectedTemplate?.filename}</div>
                        </div>
                    </div>
                }
                open={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                zIndex={100000}
                footer={[
                    <Button key="close" onClick={() => setDetailsVisible(false)}>
                        Close
                    </Button>,
                    <Button
                        key="generate"
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={() => {
                            setDetailsVisible(false);
                            navigate(`/pdf-parser/generate?templateId=${selectedTemplate?.id}`);
                        }}
                        style={{ background: "#1a1a2e" }}
                    >
                        Generate PDFs
                    </Button>,
                ]}
                width={800}
            >
                {selectedTemplate && (
                    <div>
                        <Descriptions bordered column={2} size="small" style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="Created">
                                {new Date(selectedTemplate.created_at).toLocaleString()}
                            </Descriptions.Item>
                            <Descriptions.Item label="Placeholders">
                                {selectedTemplate.placeholders?.length || 0}
                            </Descriptions.Item>
                        </Descriptions>

                        <h6 style={{ marginBottom: 12, fontWeight: 600 }}>
                            <FontSizeOutlined style={{ marginRight: 8 }} />
                            Placeholders & Fonts
                        </h6>
                        <div style={{ 
                            display: "grid", 
                            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                            gap: 8,
                            marginBottom: 16
                        }}>
                            {(selectedTemplate.placeholders || []).map((ph) => (
                                <Card 
                                    key={ph.name} 
                                    size="small" 
                                    style={{ 
                                        borderRadius: 8,
                                        background: "#fafafa"
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Tag color="blue">{`{${ph.name}}`}</Tag>
                                        <span style={{ fontSize: 12, color: "#888" }}>{ph.size}pt</span>
                                    </div>
                                    <div style={{ 
                                        fontSize: 11, 
                                        color: "#666", 
                                        marginTop: 4,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}>
                                        {ph.font}
                                    </div>
                                </Card>
                            ))}
                        </div>

                        {/* Custom Regions */}
                        {(selectedTemplate.ignore_width?._customRegions || []).length > 0 && (
                            <>
                                <h6 style={{ marginBottom: 12, fontWeight: 600 }}>
                                    <BorderOutlined style={{ marginRight: 8, color: "#722ed1" }} />
                                    Custom Regions ({selectedTemplate.ignore_width._customRegions.length})
                                </h6>
                                <div style={{ 
                                    display: "grid", 
                                    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
                                    gap: 8,
                                    marginBottom: 16
                                }}>
                                    {selectedTemplate.ignore_width._customRegions.map((region, idx) => (
                                        <Card 
                                            key={region.id || idx} 
                                            size="small" 
                                            style={{ 
                                                borderRadius: 8,
                                                background: "#f9f0ff",
                                                borderColor: "#d3adf7"
                                            }}
                                        >
                                            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                                                Page {(region.page || 0) + 1} • {Math.round(region.width)}×{Math.round(region.height)}px
                                            </div>
                                            <div style={{ 
                                                fontSize: 11, 
                                                color: "#666",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}>
                                                {region.content || "Empty region"}
                                            </div>
                                            {region.fontFamily && (
                                                <Tag size="small" style={{ marginTop: 4, fontSize: 10 }}>
                                                    <FontSizeOutlined /> {region.fontFamily}
                                                </Tag>
                                            )}
                                        </Card>
                                    ))}
                                </div>
                            </>
                        )}

                        {Object.keys(selectedTemplate.column_map || {}).length > 0 && (
                            <>
                                <h6 style={{ marginBottom: 12, fontWeight: 600 }}>
                                    <CheckCircleOutlined style={{ marginRight: 8, color: "#52c41a" }} />
                                    Saved Column Mapping
                                </h6>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                                    {Object.entries(selectedTemplate.column_map).map(([col, ph]) => (
                                        <Tag key={col}>
                                            {col} → {ph}
                                        </Tag>
                                    ))}
                                </div>
                            </>
                        )}
                        
                        {Object.keys(selectedTemplate.font_map || {}).length > 0 && (
                            <>
                                <h6 style={{ marginBottom: 12, fontWeight: 600 }}>
                                    <FontSizeOutlined style={{ marginRight: 8, color: "#1677ff" }} />
                                    Saved Font Mapping
                                </h6>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                    {Object.entries(selectedTemplate.font_map).map(([ph, font]) => (
                                        <Tag key={ph} color="blue">
                                            {ph} → {font}
                                        </Tag>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Modal>

            {/* PDF Preview Modal */}
            <Modal
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <FilePdfOutlined style={{ fontSize: 20, color: "#1a1a2e" }} />
                        <span>{previewTemplate?.name || "Template Preview"}</span>
                    </div>
                }
                open={previewVisible}
                onCancel={handlePreviewClose}
                width="90%"
                style={{ top: 20 }}
                zIndex={100000}
                bodyStyle={{ padding: 0, height: "80vh" }}
                footer={[
                    <Button key="close" onClick={handlePreviewClose}>
                        Close
                    </Button>,
                    <Button
                        key="generate"
                        type="primary"
                        icon={<PlayCircleOutlined />}
                        onClick={() => {
                            handlePreviewClose();
                            navigate(`/pdf-parser/generate?templateId=${previewTemplate?.id}`);
                        }}
                        style={{ background: "#1a1a2e" }}
                    >
                        Generate PDFs
                    </Button>,
                ]}
            >
                {previewLoading ? (
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        height: "100%",
                        minHeight: 400
                    }}>
                        <Spin 
                            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
                            tip="Loading PDF..."
                        />
                    </div>
                ) : previewUrl ? (
                    <iframe
                        src={previewUrl}
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            minHeight: "70vh"
                        }}
                        title="PDF Preview"
                    />
                ) : (
                    <div style={{ 
                        textAlign: "center", 
                        padding: 60,
                        color: "#888"
                    }}>
                        <FilePdfOutlined style={{ fontSize: 64, marginBottom: 16, color: "#ccc" }} />
                        <p>Failed to load PDF preview</p>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default TemplateList;
